import React from 'react';

import './styles.scss';
import classNames from 'classnames';

import playPause from 'utils/playPause';
import WebAudioVoice from 'utils/audioEngine';

import getUniqueId from 'utils/getUniqueId';

import {DASHARRAY_LENGTH} from 'constants/index';

import 'actions';
import strings from 'utils/strings';

const AUTOPLAY_MAX_ATTEMPTS = 3;
const AUTOPLAY_RETRY_DELAY = 250;

class AudioPlayer extends React.Component {
	constructor() {
		super();

		this.uniqueId = `audio${getUniqueId()}`;
		this.autoplayAttemptCount = 0;
		this.autoplayHasStarted = false;
		this.autoplayRetryTimer = null;
		this.hasUserInteracted = false;

		this.state = {
			isPlaying: false,
			duration: 0, // ms
			currentTime: 0,
			isDone: false,
			isStarted: false,
		};
		this.tick = this.tick.bind(this);
		this.handleAutoplayReady = this.handleAutoplayReady.bind(this);
		this.handleDurationChange = this.handleDurationChange.bind(this);
		this.handleStartPlayer = this.handleStartPlayer.bind(this);
		this.handlePause = this.handlePause.bind(this);
		this.handlePlay = this.handlePlay.bind(this);
	}

	componentDidMount() {
		this.createVoice(this.props.src);
		this.startAutoplay();
	}

	componentWillReceiveProps(nextProps) {
		const {src} = this.props;
		if (src && src !== nextProps.src) {
			this.handlePause();
		}
	}

	componentDidUpdate(prevProps) {
		const {autoplay, src} = this.props;
		if (src !== prevProps.src) {
			this.clearAutoplayRetry();
			this.detachVoiceListeners(this.voice);
			this.voice.destroy();
			this.createVoice(src);
			this.startAutoplay();
		} else if (!prevProps.autoplay && autoplay) {
			this.startAutoplay();
		} else if (prevProps.autoplay && !autoplay) {
			this.clearAutoplayRetry();
		}
	}

	componentWillUnmount() {
		this.clearAutoplayRetry();
		this.detachVoiceListeners(this.voice);
		if (this.voice) {
			this.voice.destroy();
		}
		cancelAnimationFrame(this.animationFrame);
	}

	getId() {
		return this.props.id || this.uniqueId;
	}

	createVoice(src) {
		this.voice = new WebAudioVoice(src);
		this.attachVoiceListeners(this.voice);
		// Kick decoding off immediately (mirrors a native <audio> element's
		// implicit metadata preload) so playback starts without delay once
		// play() is actually called.
		this.voice.load().catch(() => {});

		const {refs} = this.props;
		const id = this.getId();
		if (refs && id) {
			refs[id] = this.voice;
		}
	}

	attachVoiceListeners(voice) {
		voice.addEventListener('durationchange', this.handleDurationChange);
		voice.addEventListener('loadeddata', this.handleAutoplayReady);
		voice.addEventListener('canplay', this.handleAutoplayReady);
		voice.addEventListener('pause', this.handlePause);
		voice.addEventListener('play', this.handlePlay);
	}

	detachVoiceListeners(voice) {
		if (!voice) return;
		voice.removeEventListener('durationchange', this.handleDurationChange);
		voice.removeEventListener('loadeddata', this.handleAutoplayReady);
		voice.removeEventListener('canplay', this.handleAutoplayReady);
		voice.removeEventListener('pause', this.handlePause);
		voice.removeEventListener('play', this.handlePlay);
	}

	handleDurationChange() {
		const duration = this.voice && this.voice.duration;

		if (duration) {
			this.setState({
				currentTime: duration,
				duration,
			});
		}
	}

	handleAutoplayReady() {
		this.tryAutoplay();
	}

	tick() {
		const voice = this.voice;
		const hasStoppedPlaying = voice.ended || voice.paused;
		if (hasStoppedPlaying) {
			this.setState({
				isDone: true,
			});
		} else {
			this.setState({
				currentTime: voice.currentTime,
			});
		}

		if (!this.state.isDone) {
			this.animationFrame = requestAnimationFrame(this.tick);
		}
	}

	completeCallback() {
		if (this.props.onComplete) {
			this.props.onComplete();
		}
	}

	handlePlay() {
		this.autoplayHasStarted = true;
		this.clearAutoplayRetry();
		this.setState(
			{
				isDone: false,
				isPlaying: true,
			},
			() => {
				this.tick();
			}
		);

		if (typeof this.props.onStart === 'function') {
			this.props.onStart();
		}
	}

	handlePause() {
		this.setState({
			isPlaying: false,
		});

		const voice = this.voice;
		const isEnded = voice.ended;

		if (isEnded) {
			this.completeCallback();
		}

		cancelAnimationFrame(this.animationFrame);

		if (typeof this.props.onPause === 'function') {
			this.props.onPause();
		}
	}

	handleStartPlayer(isUserInteraction = true) {
		if (isUserInteraction) {
			this.hasUserInteracted = true;
			this.clearAutoplayRetry();
		}
		playPause(this.voice, this.props.refs, this.props.onErrorPlay);
	}

	startAutoplay() {
		this.clearAutoplayRetry();
		this.autoplayAttemptCount = 0;
		this.autoplayHasStarted = false;

		if (!this.props.autoplay || !this.voice) return;

		this.voice.load().catch(() => {});
		if (this.hasUserInteracted) return;

		this.tryAutoplay();
		this.autoplayRetryTimer = setTimeout(() => {
			this.autoplayRetryTimer = null;
			this.tryAutoplay();
		}, AUTOPLAY_RETRY_DELAY);
	}

	tryAutoplay() {
		if (
			!this.props.autoplay ||
			!this.voice ||
			this.hasUserInteracted ||
			this.autoplayHasStarted ||
			this.autoplayAttemptCount >= AUTOPLAY_MAX_ATTEMPTS ||
			!this.voice.paused ||
			this.voice.ended
		) {
			return;
		}

		this.autoplayAttemptCount += 1;
		this.handleStartPlayer(false);
	}

	clearAutoplayRetry() {
		if (this.autoplayRetryTimer) {
			clearTimeout(this.autoplayRetryTimer);
			this.autoplayRetryTimer = null;
		}
	}

	render() {
		const {disabled, inline, strokeColor, storkeColor, outlineColor} =
			this.props;

		const {currentTime, duration, isPlaying} = this.state;

		let progress = 0;
		if (isPlaying) {
			const percentageDone = (currentTime * 100) / duration;
			progress =
				DASHARRAY_LENGTH - (percentageDone * DASHARRAY_LENGTH) / 100;
		}

		const styleVars = {};
		const progressStroke = strokeColor || storkeColor;
		if (progressStroke) styleVars['--audio-stroke'] = progressStroke;
		if (outlineColor) styleVars['--audio-outline'] = outlineColor;

		const progressStyle = {
			strokeDashoffset: `${progress}`,
			...(progressStroke ? {stroke: progressStroke} : null),
		};

		return (
			<button
				className={classNames({
					'lit-circle': true,
					'lit-audio': true,
					'is-playing': isPlaying,
					inline: inline,
				})}
				disabled={disabled}
				onClick={() => {
					if (!this.voice) {
						return;
					}
					this.handleStartPlayer();
				}}
				title={disabled ? strings.audioMax : null}
				style={styleVars}
			>
				<svg
					className="lit-circle__interactive"
					height="90"
					style={{enableBackground: 'new 0 0 90 90'}}
					version="1.1"
					viewBox="0 0 90 90"
					width="90"
					x="0px"
					xmlSpace="preserve"
					xmlns="http://www.w3.org/2000/svg"
					xmlnsXlink="http://www.w3.org/1999/xlink"
					y="0px"
				>
					<g transform="rotate(-84 45 45)">
						<circle
							className="lit-audio__outline"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							strokeLinecap="round"
							strokeWidth="5"
						/>
						<circle
							className="lit-audio__progress"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							strokeLinecap="round"
							strokeWidth="6"
							style={progressStyle}
						/>
					</g>
				</svg>
				<div className="lit-audio__icon" />
			</button>
		);
	}
}

export default AudioPlayer;
